import { useState, useEffect, useCallback } from "react";
import Pulso from "./components/Pulso";
import ResumenGeneral from "./components/ResumenGeneral";
import AnalisisEstrategico from "./components/AnalisisEstrategico";
import TransportadorasVista from "./components/TransportadorasVista";
import TendenciaDiaria from "./components/TendenciaDiaria";
import AnalisisDiario from "./components/AnalisisDiario";
import CargaArchivos from "./components/CargaArchivos";
import CargaStock from "./components/CargaStock";
import StockEvolucion from "./components/StockEvolucion";
import AdminPanel from "./components/AdminPanel";
import AnalisisVentaLogistica from "./components/AnalisisVentaLogistica";
import DetalleProducto from "./components/DetalleProducto";
import Calculadora from "./components/Calculadora";
import LandingPage from "./components/LandingPage";
import BilleteraDropi from "./components/BilleteraDropi";
import TablaPedidos from "./components/TablaPedidos";
import ActivacionPro from "./components/ActivacionPro";
import Alertas from "./components/Alertas";
import Dashboard from "./components/Dashboard";
import {
  TrendingUp, BarChart3, Search, Truck, Calculator,
  Wallet, AlertTriangle, ListChecks, FolderUp, Lock,
  BookOpen, ChevronUp, FolderOpen, Check,
} from "lucide-react";
import { useIsMobile } from "./hooks/useIsMobile";
import { useMetaPixel } from "./hooks/useMetaPixel";

// Detectar parámetros de activación en la URL
const _urlParams = new URLSearchParams(window.location.search);
const URL_ACTIVAR = _urlParams.get("activar") === "1";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const ALL_NAV = [
  { key: "dashboard", label: "Dashboard" },
  { key: "pulso", label: "Pulso" },
  { key: "detalle-producto", label: "Productos" },
  { key: "transportadoras", label: "Transportadoras" },
  { key: "billetera", label: "Wallet" },
  { key: "alerta", label: "🚨 Alerta", pro: true },
  { key: "pedidos", label: "Pedidos" },
  { key: "calculadora", label: "Calculadora" },
  { key: "carga", label: "Archivos" },
];

// Vistas accesibles sin datos cargados
const VISTAS_SIEMPRE_ACTIVAS = new Set(["carga", "carga-stock", "calculadora", "home", "billetera", "pedidos"]);

const authFetch = (url, opts = {}) =>
  fetch(url, {
    ...opts,
    headers: {
      ...(opts.headers || {}),
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

// ── Utilidades de fecha ──────────────────────────────────────────────────────
function hoy() { return new Date().toISOString().slice(0, 10); }
function primerDia(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function ultimoDia(y, m) { return new Date(y, m, 0).toISOString().slice(0, 10); }

const PERIODOS = [
  { key: "este_mes", label: "Este mes" },
  { key: "mes_anterior", label: "Mes anterior" },
  { key: "ultimos_30", label: "Últimos 30 días" },
  { key: "personalizado", label: "Personalizado" },
];

function calcularRango(key) {
  const hd = new Date();
  if (key === "este_mes") return { desde: primerDia(hd), hasta: hoy() };
  if (key === "mes_anterior") {
    const y = hd.getMonth() === 0 ? hd.getFullYear() - 1 : hd.getFullYear();
    const m = hd.getMonth() === 0 ? 12 : hd.getMonth();
    return { desde: `${y}-${String(m).padStart(2, "0")}-01`, hasta: ultimoDia(y, m) };
  }
  if (key === "ultimos_30") {
    const d30 = new Date(hd); d30.setDate(d30.getDate() - 29);
    return { desde: d30.toISOString().slice(0, 10), hasta: hoy() };
  }
  return null;
}

export default function App() {
  const isMobile = useIsMobile();
  const { trackEvent } = useMetaPixel();
  const [usuario, setUsuario] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [showLanding, setShowLanding] = useState(!localStorage.getItem("token"));

  const [resumen, setResumen] = useState(null);
  const [productos, setProductos] = useState([]);
  const [transp, setTransp] = useState([]);
  const [diario, setDiario] = useState([]);
  const [stockData, setStockData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [vista, setVista] = useState("resumen");
  const [showUpgrade, setShowUpgrade] = useState(false);

  // ── Estado global de período ────────────────────────────────────────────────
  const [periodoKey, setPeriodoKey] = useState("este_mes");
  const [desde, setDesde] = useState(() => primerDia());
  const [hasta, setHasta] = useState(() => hoy());
  const [desdeTmp, setDesdeTmp] = useState(() => primerDia());
  const [hastaTmp, setHastaTmp] = useState(() => hoy());

  const handlePeriodoKey = (key) => {
    setPeriodoKey(key);
    if (key !== "personalizado") {
      const rango = calcularRango(key);
      setDesde(rango.desde);
      setHasta(rango.hasta);
      setDesdeTmp(rango.desde);
      setHastaTmp(rango.hasta);
    }
  };

  const aplicarPersonalizado = () => {
    setDesde(desdeTmp);
    setHasta(hastaTmp);
  };

  // ── Verificar sesión al montar ──────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { setAuthReady(true); return; }

    fetch(`${API_URL}/api/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(user => {
        if (user) { setUsuario(user); setShowLanding(false); }
        else { localStorage.removeItem("token"); setShowLanding(true); }
      })
      .catch(() => { localStorage.removeItem("token"); setShowLanding(true); })
      .finally(() => setAuthReady(true));
  }, []);

  // ── Cargar datos del dashboard ──────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    if (!usuario) return;
    try {
      setLoading(true);
      setError(null);

      const estadoRes = await authFetch(`${API_URL}/api/estado`);
      if (estadoRes.status === 401) {
        localStorage.removeItem("token");
        setUsuario(null);
        return;
      }
      if (!estadoRes.ok) throw new Error("No se pudo conectar al backend");
      const estado = await estadoRes.json();

      if (!estado.tiene_datos) {
        setVista("carga");
        setLoading(false);
        return;
      }

      const [r1, r2, r3, r4, r5] = await Promise.all([
        authFetch(`${API_URL}/api/resumen`).then(r => r.json()),
        authFetch(`${API_URL}/api/productos`).then(r => r.json()),
        authFetch(`${API_URL}/api/transportadoras`).then(r => r.json()),
        authFetch(`${API_URL}/api/diario`).then(r => r.json()),
        authFetch(`${API_URL}/api/stock`).then(r => r.json()).catch(() => null),
      ]);
      setResumen(r1);
      setProductos(r2);
      setTransp(r3);
      setDiario(r4);
      setStockData(r5);
    } catch (e) {
      setError(e.message || "No se pudo conectar al backend");
    } finally {
      setLoading(false);
    }
  }, [usuario]);

  useEffect(() => { if (usuario) fetchAll(); }, [usuario, fetchAll]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  // Registrar navegación (fire-and-forget, no bloquea la UI)
  const logNavegacion = (vistaKey) => {
    const token = localStorage.getItem("token");
    if (!token) return;
    fetch(`${API_URL}/api/log/navegacion`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ vista: vistaKey }),
    }).catch(() => { });
  };

  const handleLogin = user => {
    setUsuario(user);
    setVista("home");
    setShowLanding(false);

    // Tracking Meta: registro/login completado
    const [first_name, ...rest] = (user.nombre || "").split(" ");
    trackEvent('Lead', {
      email: user.email,
      user_id: String(user.id || ""),
      first_name: first_name || undefined,
      last_name: rest.join(" ") || undefined,
    });
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setUsuario(null);
    setResumen(null);
    setProductos([]);
    setTransp([]);
    setDiario([]);
    setStockData(null);
    setVista("resumen");
  };

  const onGeneracionExitosa = () => {
    setVista("pulso");
    fetchAll();
  };

  // ── Estados de carga ────────────────────────────────────────────────────────
  if (!authReady) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "sans-serif", color: "#888" }}>
      <div style={{ width: 28, height: 28, border: "3px solid #eee", borderTop: "3px solid #1D9E75", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  // Página de activación post-pago (zenviq.app?activar=1)
  if (!usuario && URL_ACTIVAR) {
    return (
      <ActivacionPro
        onActivado={u => {
          setUsuario(u);
          setVista("home");
          setShowLanding(false);
          window.history.replaceState({}, "", "/");
        }}
      />
    );
  }

  if (!usuario && showLanding) {
    return <LandingPage onLogin={handleLogin} />;
  }

  if (!usuario) return <LandingPage onLogin={handleLogin} />;

  // ── Módulos habilitados para este usuario ───────────────────────────────────
  const modulosUser = new Set(usuario.modulos || []);
  const esAdmin = usuario.rol === "admin";
  const hayDatos = resumen !== null;
  const esPro = usuario.plan === "pro" || esAdmin;

  // Admin ve todos los módulos; Pro ve los propios + tabs marcados pro:true; Free solo los habilitados
  const navItems = ALL_NAV.filter(n => esAdmin || modulosUser.has(n.key) || (esPro && n.pro));

  const vistaActiva = (vista === "admin" && esAdmin) ? "admin"
    : vista === "home" ? "home"
      : navItems.find(n => n.key === vista) ? vista
        : "home";

  // Vistas que usan el selector de período global
  const vistasConPeriodo = new Set(["dashboard", "pulso", "detalle-producto", "billetera", "alerta"]);
  const mostrarSelectorPeriodo = vistasConPeriodo.has(vistaActiva);

  if (loading) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", gap: 16, fontFamily: "sans-serif", color: "#888" }}>
      <div style={{ width: 32, height: 32, border: "3px solid #eee", borderTop: "3px solid #1D9E75", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <p>Cargando datos...</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", gap: 8, fontFamily: "sans-serif" }}>
      <p style={{ color: "#E24B4A", fontSize: 16 }}>⚠️ {error}</p>
      <button onClick={fetchAll} style={{ fontSize: 12, padding: "6px 14px", borderRadius: 8, border: "0.5px solid #ddd", background: "transparent", cursor: "pointer" }}>
        Reintentar
      </button>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#F0F2FF" }}>

      {/* ── Navbar Zenviq ─────────────────────────────────────────────────────── */}
      <div style={{ background: "#fff", borderBottom: "1px solid #E5E7EB", position: "sticky", top: 0, zIndex: 200 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 1.5rem", position: "relative" }}>

          {/* Header row */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 60, height: "auto", paddingTop: 8, paddingBottom: 8 }}>

            {/* Logo */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <svg width={28} height={28} viewBox="0 0 40 40" fill="none">
                <rect width="40" height="40" rx="10" fill="#059669" />
                <polyline points="10,11 30,11 10,29 30,29" stroke="white" strokeWidth="3.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                <polyline points="9,36 15,32 22,34 31,28" stroke="rgba(255,255,255,0.45)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              </svg>
              <span style={{ fontSize: "clamp(15px, 3vw, 17px)", fontWeight: 800, color: "#111827", letterSpacing: "-0.04em", fontFamily: "'Space Grotesk', sans-serif" }}>Zenviq</span>
              {!isMobile && resumen?.ultima_actualizacion && (
                <span style={{ fontSize: 11, color: "#9CA3AF", marginLeft: 8 }}>
                  Actualizado: {new Date(resumen.ultima_actualizacion).toLocaleString("es-CL")}
                </span>
              )}
            </div>

            {/* Derecha: acciones + hamburger en mobile */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {!isMobile && (
                <div style={{ textAlign: "right", marginRight: 4 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>{usuario.nombre}</div>
                  <div style={{ fontSize: 11, color: "#9CA3AF" }}>
                    {usuario.plan === "pro" || usuario.rol === "admin" ? "Plan Pro" : "Plan Free"}
                  </div>
                </div>
              )}
              <button onClick={fetchAll} style={{
                fontSize: 14, padding: "6px 10px", borderRadius: 8, cursor: "pointer",
                border: "1px solid #E5E7EB", background: "#fff", color: "#6B7280",
              }}>↻</button>
              <button onClick={handleLogout} style={{
                fontSize: 13, padding: "6px 12px", borderRadius: 8, cursor: "pointer",
                border: "1px solid #E5E7EB", background: "#fff", color: "#6B7280",
              }}>Salir</button>

              {/* Hamburger — solo en mobile */}
              {isMobile && (
                <button
                  onClick={() => setMenuAbierto(v => !v)}
                  style={{
                    display: "flex", flexDirection: "column", justifyContent: "center",
                    alignItems: "center", gap: 5, width: 38, height: 38,
                    border: "1px solid #E5E7EB", borderRadius: 8, background: menuAbierto ? "#F0F2FF" : "#fff",
                    cursor: "pointer", padding: 0, flexShrink: 0,
                  }}
                  aria-label="Menú"
                >
                  {/* 3 líneas → X cuando abierto */}
                  {menuAbierto ? (
                    <span style={{ fontSize: 18, color: "#059669", lineHeight: 1, fontWeight: 700 }}>✕</span>
                  ) : (
                    <>
                      <span style={{ display: "block", width: 18, height: 2, background: "#374151", borderRadius: 2 }} />
                      <span style={{ display: "block", width: 18, height: 2, background: "#374151", borderRadius: 2 }} />
                      <span style={{ display: "block", width: 18, height: 2, background: "#374151", borderRadius: 2 }} />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Nav tabs — solo desktop */}
          {!isMobile && (
            <div style={{ display: "flex", gap: 0, alignItems: "flex-end" }}>
              <button
                onClick={() => { setVista("home"); logNavegacion("home"); }}
                style={{
                  fontSize: 14, padding: "10px 18px",
                  cursor: "pointer", border: "none", background: "transparent", whiteSpace: "nowrap",
                  color: vistaActiva === "home" ? "#059669" : "#6B7280",
                  fontWeight: vistaActiva === "home" ? 700 : 400,
                  borderBottom: vistaActiva === "home" ? "2px solid #059669" : "2px solid transparent",
                  fontFamily: "inherit", transition: "color 0.15s, border-color 0.15s", flexShrink: 0,
                }}
              >Inicio</button>

              {navItems.map(n => {
                const sinDatos = !hayDatos && !VISTAS_SIEMPRE_ACTIVAS.has(n.key);
                const handleClick = () => { const v = sinDatos ? "carga" : n.key; setVista(v); logNavegacion(v); };
                return (
                  <button
                    key={n.key}
                    onClick={handleClick}
                    title={sinDatos ? "Primero carga los archivos" : undefined}
                    style={{
                      fontSize: 14, padding: "10px 18px",
                      cursor: sinDatos ? "not-allowed" : "pointer",
                      border: "none", background: "transparent", whiteSpace: "nowrap", flexShrink: 0,
                      color: sinDatos ? "#D1D5DB" : vistaActiva === n.key ? "#059669" : "#6B7280",
                      fontWeight: vistaActiva === n.key ? 700 : 400,
                      borderBottom: vistaActiva === n.key ? "2px solid #059669" : "2px solid transparent",
                      fontFamily: "inherit", transition: "color 0.15s, border-color 0.15s",
                    }}
                  >{n.label}</button>
                );
              })}

              {esAdmin && (
                <button onClick={() => { setVista("admin"); logNavegacion("admin"); }} style={{
                  fontSize: 14, padding: "10px 18px", cursor: "pointer", marginLeft: "auto", flexShrink: 0,
                  border: "none", background: "transparent", whiteSpace: "nowrap",
                  color: vistaActiva === "admin" ? "#059669" : "#6B7280",
                  fontWeight: vistaActiva === "admin" ? 700 : 400,
                  borderBottom: vistaActiva === "admin" ? "2px solid #059669" : "2px solid transparent",
                  fontFamily: "inherit",
                }}>Admin</button>
              )}
            </div>
          )}

          {/* Drawer mobile — desplegable bajo el header */}
          {isMobile && menuAbierto && (
            <div style={{
              position: "absolute", top: "100%", left: 0, right: 0,
              background: "#fff", borderTop: "1px solid #E5E7EB",
              boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
              zIndex: 201, borderRadius: "0 0 16px 16px",
              overflow: "hidden",
            }}>
              {/* Item: Inicio */}
              <button
                onClick={() => { setVista("home"); logNavegacion("home"); setMenuAbierto(false); }}
                style={{
                  display: "flex", alignItems: "center", width: "100%", padding: "14px 20px",
                  border: "none", borderBottom: "1px solid #F3F4F6",
                  background: vistaActiva === "home" ? "#ECFDF5" : "#fff",
                  color: vistaActiva === "home" ? "#059669" : "#374151",
                  fontWeight: vistaActiva === "home" ? 700 : 400,
                  fontSize: 15, cursor: "pointer", fontFamily: "inherit",
                  textAlign: "left", gap: 12,
                }}
              >
                {vistaActiva === "home" && <span style={{ width: 3, height: 20, background: "#059669", borderRadius: 2, flexShrink: 0 }} />}
                Inicio
              </button>

              {/* Items del nav */}
              {navItems.map(n => {
                const sinDatos = !hayDatos && !VISTAS_SIEMPRE_ACTIVAS.has(n.key);
                const activo = vistaActiva === n.key;
                return (
                  <button
                    key={n.key}
                    onClick={() => {
                      if (!sinDatos) { setVista(n.key); logNavegacion(n.key); }
                      else { setVista("carga"); logNavegacion("carga"); }
                      setMenuAbierto(false);
                    }}
                    style={{
                      display: "flex", alignItems: "center", width: "100%", padding: "14px 20px",
                      border: "none", borderBottom: "1px solid #F3F4F6",
                      background: activo ? "#ECFDF5" : "#fff",
                      color: sinDatos ? "#D1D5DB" : activo ? "#059669" : "#374151",
                      fontWeight: activo ? 700 : 400,
                      fontSize: 15, cursor: sinDatos ? "not-allowed" : "pointer",
                      fontFamily: "inherit", textAlign: "left", gap: 12,
                    }}
                  >
                    {activo && <span style={{ width: 3, height: 20, background: "#059669", borderRadius: 2, flexShrink: 0 }} />}
                    {n.label}
                    {sinDatos && <span style={{ marginLeft: "auto", fontSize: 11, color: "#9CA3AF" }}>Sin datos</span>}
                  </button>
                );
              })}

              {/* Admin */}
              {esAdmin && (
                <button
                  onClick={() => { setVista("admin"); logNavegacion("admin"); setMenuAbierto(false); }}
                  style={{
                    display: "flex", alignItems: "center", width: "100%", padding: "14px 20px",
                    border: "none", background: vistaActiva === "admin" ? "#ECFDF5" : "#fff",
                    color: vistaActiva === "admin" ? "#059669" : "#374151",
                    fontWeight: vistaActiva === "admin" ? 700 : 400,
                    fontSize: 15, cursor: "pointer", fontFamily: "inherit", textAlign: "left", gap: 12,
                  }}
                >
                  {vistaActiva === "admin" && <span style={{ width: 3, height: 20, background: "#059669", borderRadius: 2, flexShrink: 0 }} />}
                  Admin
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Backdrop — cierra el menú al tocar fuera */}
      {isMobile && menuAbierto && (
        <div
          onClick={() => setMenuAbierto(false)}
          style={{ position: "fixed", inset: 0, zIndex: 199, background: "rgba(0,0,0,0.25)" }}
        />
      )}

      {/* ── Contenido principal ───────────────────────────────────────────────── */}
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "1.5rem" }}>

        {/* Selector de período global */}
        {mostrarSelectorPeriodo && (
          <div style={{
            background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12,
            padding: "0.75rem 1rem", marginBottom: "1rem",
            display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap"
          }}>
            <span style={{ fontSize: 12, color: "#6B7280", fontWeight: 600, marginRight: 4 }}>Período:</span>
            {PERIODOS.map(p => (
              <button key={p.key} onClick={() => handlePeriodoKey(p.key)} style={{
                fontSize: 13, padding: "5px 14px", borderRadius: 20, cursor: "pointer",
                border: periodoKey === p.key ? "none" : "1px solid #E5E7EB",
                background: periodoKey === p.key ? "#059669" : "transparent",
                color: periodoKey === p.key ? "#fff" : "#6B7280",
                fontWeight: periodoKey === p.key ? 600 : 400, fontFamily: "inherit",
              }}>{p.label}</button>
            ))}
            {periodoKey === "personalizado" && (
              <>
                <input type="date" value={desdeTmp} onChange={e => setDesdeTmp(e.target.value)}
                  style={{
                    fontSize: 13, padding: "5px 10px", borderRadius: 8,
                    border: "1px solid #E5E7EB", outline: "none", color: "#111827"
                  }} />
                <span style={{ fontSize: 13, color: "#D1D5DB" }}>→</span>
                <input type="date" value={hastaTmp} onChange={e => setHastaTmp(e.target.value)}
                  style={{
                    fontSize: 13, padding: "5px 10px", borderRadius: 8,
                    border: "1px solid #E5E7EB", outline: "none", color: "#111827"
                  }} />
                <button onClick={aplicarPersonalizado} style={{
                  fontSize: 13, padding: "5px 14px", borderRadius: 8, border: "none",
                  background: "#059669", color: "#fff", cursor: "pointer", fontFamily: "inherit",
                }}>Aplicar</button>
              </>
            )}
            <span style={{ fontSize: 12, color: "#9CA3AF", marginLeft: 4 }}>{desde} → {hasta}</span>
          </div>
        )}

        {/* Guardia: sin datos cargados */}
        {!hayDatos && !VISTAS_SIEMPRE_ACTIVAS.has(vistaActiva) && vistaActiva !== "admin" && (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            minHeight: 400, gap: 16, textAlign: "center",
          }}>
            <FolderOpen size={48} color="#9CA3AF" strokeWidth={1.5} />
            <div style={{ fontSize: 20, fontWeight: 700, color: "#111827" }}>Aún no hay datos cargados</div>
            <p style={{ fontSize: 15, color: "#6B7280", maxWidth: 360, margin: 0, lineHeight: 1.6 }}>
              Sube tus archivos de Dropi y Meta Ads para ver esta vista.
            </p>
            <button onClick={() => setVista("carga")} style={{
              marginTop: 8, padding: "12px 32px", borderRadius: 10, border: "none",
              background: "#059669", color: "#fff", fontSize: 15, fontWeight: 600, cursor: "pointer",
            }}>Cargar archivos →</button>
          </div>
        )}

        {/* Vistas */}
        {vistaActiva === "home" && <HomeInicio usuario={usuario} modulosUser={modulosUser} hayDatos={hayDatos} onNavegar={v => { setVista(v); logNavegacion(v); }} onUpgrade={() => setShowUpgrade(true)} />}
        {vistaActiva === "dashboard" && hayDatos && <Dashboard diario={diario} desde={desde} hasta={hasta} />}
        {vistaActiva === "pulso" && hayDatos && <Pulso productos={productos} desde={desde} hasta={hasta} />}
        {vistaActiva === "alerta" && hayDatos && (esPro
          ? <Alertas productos={productos} desde={desde} hasta={hasta} onNavegar={v => { setVista(v); logNavegacion(v); }} />
          : <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 300, gap: 14, textAlign: "center" }}>
            <div style={{ fontSize: 40 }}>🔒</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>Disponible en plan Pro</div>
            <p style={{ fontSize: 14, color: "#6B7280", maxWidth: 340, margin: 0, lineHeight: 1.6 }}>
              Las alertas tempranas detectan automáticamente ROAS bajo, márgenes negativos y problemas de entrega.
            </p>
            <button onClick={() => setShowUpgrade(true)} style={{ padding: "10px 28px", borderRadius: 10, border: "none", background: "#059669", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
              Activar plan Pro →
            </button>
          </div>
        )}
        {vistaActiva === "resumen" && hayDatos && <ResumenGeneral resumen={resumen} />}
        {vistaActiva === "estrategico" && hayDatos && <AnalisisEstrategico productos={productos} />}
        {vistaActiva === "diario" && hayDatos && <AnalisisDiario datos={diario} />}
        {vistaActiva === "tendencia" && hayDatos && <TendenciaDiaria datos={diario} />}
        {vistaActiva === "transportadoras" && hayDatos && <TransportadorasVista datos={transp} />}
        {vistaActiva === "stock" && hayDatos && <StockEvolucion datos={stockData} />}
        {vistaActiva === "venta-logistica" && hayDatos && <AnalisisVentaLogistica />}
        {vistaActiva === "detalle-producto" && hayDatos && <DetalleProducto desde={desde} hasta={hasta} />}
        {vistaActiva === "calculadora" && <Calculadora resumen={esPro ? resumen : null} modoPro={esPro} />}
        {vistaActiva === "billetera" && <BilleteraDropi desde={desde} hasta={hasta} />}
        {vistaActiva === "pedidos" && <TablaPedidos />}
        {vistaActiva === "carga" && <CargaArchivos apiUrl={API_URL} onExito={onGeneracionExitosa} />}
        {vistaActiva === "carga-stock" && <CargaStock />}
        {vistaActiva === "admin" && esAdmin && <AdminPanel />}
      </div>

      {/* Modal upgrade */}
      {showUpgrade && (
        <ModalUpgrade
          onClose={() => setShowUpgrade(false)}
          onPlanActivado={u => { setUsuario(u); setShowUpgrade(false); }}
          email={usuario?.email}
        />
      )}
    </div>
  );
}

// ── Módulos informativos en la home ─────────────────────────────────────────
const MODULOS_INFO = [
  {
    key: "dashboard",
    Icon: TrendingUp,
    titulo: "Dashboard",
    desc: "Resumen visual de tu negocio: pedidos por día, productos más vendidos, margen real y fletes fallidos en 4 gráficos filtrables por período.",
    libre: false,
  },
  {
    key: "pulso",
    Icon: BarChart3,
    titulo: "Pulso del negocio",
    desc: "¿Cómo está tu operación hoy? Pedidos, entregas, ingresos y ROAS en un solo vistazo.",
    libre: false,
  },
  {
    key: "detalle-producto",
    Icon: Search,
    titulo: "Análisis por Producto",
    desc: "¿Cuál producto te deja más dinero? Ve el margen real de cada uno, con y sin publicidad.",
    libre: false,
  },
  {
    key: "transportadoras",
    Icon: Truck,
    titulo: "Rentabilidad Transportadoras",
    desc: "¿Qué transportadora te conviene más? Compara tasa de entrega y costo real de cada una.",
    libre: false,
  },
  {
    key: "calculadora",
    Icon: Calculator,
    titulo: "Calculadora",
    desc: "¿A qué precio vender para no perder? Calcula el precio mínimo real antes de publicar. Incluye todos los costos que la mayoría ignora.",
    libre: true,
  },
  {
    key: "billetera",
    Icon: Wallet,
    titulo: "Wallet Dropi 2.0",
    desc: "¿Cuánto entró, cuánto salió, cuánto retiraste? Todos los movimientos de tu billetera Dropi en un vistazo.",
    libre: true,
  },
  {
    key: "alerta",
    Icon: AlertTriangle,
    titulo: "Alertas Tempranas",
    desc: "Detección automática de ROAS bajo, márgenes negativos y problemas de entrega. Con desglose explicado.",
    libre: false,
  },
  {
    key: "pedidos",
    Icon: ListChecks,
    titulo: "Tabla de Pedidos",
    desc: "Busca cualquier pedido por número, producto o guía. Todos los registros de tu archivo Dropi en una tabla ordenable.",
    libre: true,
  },
  {
    key: "carga",
    Icon: FolderUp,
    titulo: "Cargar Archivos",
    desc: "Sube tus archivos de Dropi y Meta Ads. Zenviq los cruza y genera todos los análisis.",
    libre: false,
  },
];

// ── Onboarding: pasos guiados en la home ────────────────────────────────────
const FLAG_GUIA = "zenviq:guia_oculta";

const PASOS_ZENVIQ = [
  {
    n: 1,
    Icon: FolderUp,
    titulo: "Carga tus archivos",
    desc: "Sube tu reporte de Dropi y el resumen de Meta Ads. Zenviq los cruza y todos los análisis aparecen automáticamente.",
    modulos: "Carga de archivos",
    cta: "Empezar",
    vista: "carga",
    pro: false,
  },
  {
    n: 2,
    Icon: BarChart3,
    titulo: "Mira tu rentabilidad real",
    desc: "Pulso te muestra pedidos, entregas, ingresos y ROAS reales del período. El Dashboard suma 4 gráficos clave para ver el ritmo del negocio de un vistazo.",
    modulos: "Pulso · Dashboard",
    cta: "Abrir Pulso",
    vista: "pulso",
    pro: true,
  },
  {
    n: 3,
    Icon: TrendingUp,
    titulo: "Producto a producto, ¿cuál te deja dinero?",
    desc: "Detalle Producto te muestra el margen real de cada SKU descontando publicidad y fletes. Identifica los ganadores y los que están sangrando.",
    modulos: "Detalle Producto",
    cta: "Ver detalle",
    vista: "detalle-producto",
    pro: true,
  },
  {
    n: 4,
    Icon: AlertTriangle,
    titulo: "Monitorea tu operación",
    desc: "Alertas te avisa cuando algo va mal: ROAS bajo, margen negativo, baja entrega o audiencia inflada. Cada alerta te lleva al producto o vista relevante para tomar acción. La Billetera y Transportadoras quedan accesibles desde el grid de abajo.",
    modulos: "Alertas · Billetera · Transportadoras",
    cta: "Ver alertas",
    vista: "alerta",
    pro: true,
  },
];

function PasoCard({ paso, esPro, completado, onNavegar, isMobile }) {
  const { n, Icon, titulo, desc, modulos, cta, vista, pro } = paso;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "flex-start" : "center",
        gap: isMobile ? 12 : 18,
        background: completado ? "#F9FAFB" : "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: 14,
        padding: isMobile ? "1rem" : "1.1rem 1.25rem",
      }}
    >
      {/* Número o check si completado */}
      <div style={{
        flex: "0 0 auto", width: 52, height: 52, borderRadius: "50%",
        background: completado ? "#ECFDF5" : "#059669",
        color: completado ? "#059669" : "#fff",
        border: completado ? "2px solid #6EE7B7" : "none",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 22, fontWeight: 800,
      }}>
        {completado ? <Check size={26} strokeWidth={3} /> : n}
      </div>

      {/* Centro */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
          <Icon size={16} strokeWidth={2.25} color="#059669" />
          <span style={{ fontSize: 15, fontWeight: 800, color: "#111827", letterSpacing: "-0.01em" }}>
            {titulo}
          </span>
          {pro && !esPro && (
            <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 20, background: "#F3F4F6", color: "#6B7280", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 3 }}>
              <Lock size={9} strokeWidth={2.5} /> Pro
            </span>
          )}
        </div>
        <p style={{ fontSize: 13.5, color: "#6B7280", lineHeight: 1.6, margin: "0 0 8px" }}>
          {desc}
        </p>
        <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: 20, background: "#F9FAFB", color: "#6B7280", fontWeight: 600, border: "1px solid #E5E7EB" }}>
          {modulos}
        </span>
      </div>

      {/* CTA */}
      <button
        onClick={() => onNavegar(vista)}
        style={{
          flex: "0 0 auto",
          padding: "9px 16px", borderRadius: 8, border: "1px solid #6EE7B7",
          background: "#ECFDF5", color: "#059669", fontSize: 13, fontWeight: 700,
          cursor: "pointer", whiteSpace: "nowrap",
          alignSelf: isMobile ? "stretch" : "center",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = "#D1FAE5"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "#ECFDF5"; }}
      >
        {cta} →
      </button>
    </div>
  );
}

function HomeInicio({ usuario, modulosUser, hayDatos, onNavegar, onUpgrade }) {
  const esPro = usuario.plan === "pro" || usuario.rol === "admin";
  const isMobile = useIsMobile();
  const [guiaOculta, setGuiaOculta] = useState(() => localStorage.getItem(FLAG_GUIA) === "1");

  const ocultarGuia = () => {
    localStorage.setItem(FLAG_GUIA, "1");
    setGuiaOculta(true);
  };
  const verGuia = () => {
    localStorage.removeItem(FLAG_GUIA);
    setGuiaOculta(false);
  };

  return (
    <div style={{ maxWidth: 960, margin: "0 auto", padding: "2.5rem 0" }}>
      {/* Bienvenida */}
      <div style={{ marginBottom: "2rem" }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: "#111827", margin: "0 0 8px", letterSpacing: "-0.03em" }}>
          Hola, {usuario.nombre.split(" ")[0]} 👋
        </h1>
        <p style={{ fontSize: 16, color: "#6B7280", margin: 0 }}>
          {esPro
            ? "Acceso completo activo. ¿Qué querés analizar hoy?"
            : "Plan Free activo. Usa la Calculadora o activa Pro para ver todos los módulos."}
        </p>
        {!esPro && (
          <button onClick={onUpgrade} style={{
            marginTop: 14, padding: "10px 28px", borderRadius: 10, border: "none",
            background: "#059669", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer",
          }}>
            Activar plan Pro →
          </button>
        )}
      </div>

      {/* Guía paso a paso */}
      {guiaOculta ? (
        <button
          onClick={verGuia}
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "10px 18px", borderRadius: 10, border: "1px solid #E5E7EB",
            background: "#fff", color: "#059669", fontSize: 13.5, fontWeight: 700,
            cursor: "pointer", marginBottom: "2rem",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "#ECFDF5"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}
        >
          <BookOpen size={16} strokeWidth={2.25} />
          Ver guía paso a paso
        </button>
      ) : (
        <div style={{
          background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 18,
          padding: isMobile ? "1.25rem" : "1.5rem 1.75rem",
          marginBottom: "2.25rem",
        }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <BookOpen size={20} strokeWidth={2.25} color="#059669" />
              <span style={{ fontSize: 17, fontWeight: 800, color: "#111827", letterSpacing: "-0.02em" }}>
                Cómo usar Zenviq en 4 pasos
              </span>
            </div>
            <button
              onClick={ocultarGuia}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "6px 12px", borderRadius: 8, border: "1px solid transparent",
                background: "transparent", color: "#6B7280", fontSize: 12.5, fontWeight: 600,
                cursor: "pointer",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#E5E7EB"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "transparent"; }}
            >
              Ocultar guía
              <ChevronUp size={14} strokeWidth={2.5} />
            </button>
          </div>

          {/* Pasos */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {PASOS_ZENVIQ.map(p => (
              <PasoCard
                key={p.n}
                paso={p}
                esPro={esPro}
                completado={p.vista === "carga" && hayDatos}
                onNavegar={onNavegar}
                isMobile={isMobile}
              />
            ))}
          </div>
        </div>
      )}

      {/* Separador */}
      <div style={{
        marginBottom: 16, fontSize: 12, fontWeight: 700, color: "#6B7280",
        letterSpacing: "0.05em", textTransform: "uppercase",
      }}>
        Todos los módulos
      </div>

      {/* Cards de módulos */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(clamp(220px, 90%, 280px), 1fr))", gap: "clamp(12px, 3vw, 18px)" }}>
        {MODULOS_INFO.map(m => {
          const accesible = esPro ? true : modulosUser.has(m.key);
          return (
            <div
              key={m.key}
              onClick={() => accesible ? onNavegar(m.key) : onUpgrade()}
              style={{
                background: "#fff",
                border: accesible ? "1.5px solid #E5E7EB" : "1.5px dashed #E5E7EB",
                borderRadius: 18, padding: "1.75rem",
                cursor: "pointer", opacity: accesible ? 1 : 0.65,
                transition: "box-shadow 0.15s, transform 0.1s, border-color 0.15s",
                position: "relative",
              }}
              onMouseEnter={e => {
                if (accesible) {
                  e.currentTarget.style.boxShadow = "0 8px 32px rgba(5,150,105,0.12)";
                  e.currentTarget.style.borderColor = "#10B981";
                  e.currentTarget.style.transform = "translateY(-3px)";
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.borderColor = "#E5E7EB";
                e.currentTarget.style.transform = "none";
              }}
            >
              <div style={{ position: "absolute", top: 16, right: 16 }}>
                {m.libre ? (
                  <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "#ECFDF5", color: "#059669", fontWeight: 700, border: "1px solid #6EE7B7" }}>Free</span>
                ) : (
                  <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: accesible ? "#ECFDF5" : "#F3F4F6", color: accesible ? "#059669" : "#9CA3AF", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
                    {accesible ? "Pro ✓" : (<><Lock size={11} strokeWidth={2.5} /> Pro</>)}
                  </span>
                )}
              </div>

              <div style={{ marginBottom: 14, color: accesible ? "#059669" : "#9CA3AF" }}>
                <m.Icon size={32} strokeWidth={1.75} />
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: accesible ? "#111827" : "#9CA3AF", marginBottom: 8, letterSpacing: "-0.02em" }}>
                {m.titulo}
              </div>
              <p style={{ fontSize: 14, color: "#6B7280", lineHeight: 1.65, margin: 0 }}>
                {m.desc}
              </p>
              <div style={{ marginTop: 16, fontSize: 13, color: accesible ? "#059669" : "#D1D5DB", fontWeight: 600 }}>
                {accesible ? "Abrir →" : "Disponible en Pro"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function ModalUpgrade({ onClose, onPlanActivado, email }) {
  const BASE_PAYMENT_URL = process.env.REACT_APP_PAYMENT_URL || "#";
  // Hotmart acepta ?email= para pre-llenar el checkout con el email del usuario
  const PAYMENT_URL = BASE_PAYMENT_URL !== "#" && email
    ? `${BASE_PAYMENT_URL}?email=${encodeURIComponent(email)}`
    : BASE_PAYMENT_URL;
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";
  const [codigo, setCodigo] = useState("");
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [exito, setExito] = useState("");

  const handleCanjear = async () => {
    if (!codigo.trim()) return;
    setCargando(true);
    setError("");
    setExito("");
    try {
      const res = await fetch(`${API_URL}/api/canjear-codigo`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ codigo: codigo.trim().toUpperCase() }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error al canjear el código");

      // Tracking Meta: conversión a Pro
      if (window.fbq) {
        const eventId = 'sub-' + Date.now() + '-' + Math.random().toString(36).slice(2);
        window.fbq('track', 'Subscribe', {
          value: 14.99,
          currency: 'USD',
        }, { eventID: eventId });

        fetch(`${API_URL}/meta/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            event_name: 'Subscribe',
            event_id: eventId,
            email: email,
            user_id: String(data.usuario?.id || ""),
            value: 14.99,
            currency: 'USD',
            event_source_url: window.location.href,
          }),
        }).catch(() => { });
      }

      setExito(`¡Plan ${data.plan.toUpperCase()} activado! Los módulos ya están disponibles.`);
      setTimeout(() => onPlanActivado(data.usuario), 1500);
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 16, padding: "2rem 2.25rem",
          width: 400, textAlign: "center", boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
        }}
      >
        <div style={{ fontSize: 36, marginBottom: 12 }}>🔒</div>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 8px", color: "#1E1E2E" }}>
          Módulo Pro
        </h2>
        <p style={{ fontSize: 14, color: "#666", margin: "0 0 1.25rem", lineHeight: 1.6 }}>
          Este módulo está disponible en el plan <strong>Pro</strong>.<br />
          Actualiza tu plan para acceder a todos los análisis.
        </p>
        <a
          href={PAYMENT_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            if (window.fbq) {
              window.fbq('track', 'InitiateCheckout', {
                value: 14.99,
                currency: 'USD',
              });
            }
          }}
          style={{
            display: "block", padding: "11px 0", borderRadius: 9, border: "none",
            background: "#4338CA", color: "#fff", fontSize: 14, fontWeight: 600,
            textDecoration: "none", marginBottom: "1.25rem", cursor: "pointer",
          }}
        >
          Ver planes y precios →
        </a>

        {/* Sección código de activación */}
        <div style={{ borderTop: "1px solid #f0f0ee", paddingTop: "1.1rem", textAlign: "left" }}>
          <p style={{ fontSize: 12, color: "#999", margin: "0 0 8px", textAlign: "center" }}>
            ¿Ya tienes un código de activación?
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={codigo}
              onChange={e => { setCodigo(e.target.value); setError(""); setExito(""); }}
              onKeyDown={e => e.key === "Enter" && handleCanjear()}
              placeholder="PRO-XXXXXXXX"
              style={{
                flex: 1, padding: "9px 12px", borderRadius: 8, fontSize: 13,
                border: error ? "1px solid #E24B4A" : "1px solid #ddd",
                outline: "none", fontFamily: "monospace", letterSpacing: "0.05em",
                textTransform: "uppercase",
              }}
            />
            <button
              onClick={handleCanjear}
              disabled={cargando || !codigo.trim() || !!exito}
              style={{
                padding: "9px 16px", borderRadius: 8, border: "none",
                background: exito ? "#1D9E75" : "#111", color: "#fff",
                fontSize: 13, fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap",
              }}
            >
              {cargando ? "..." : exito ? "✓" : "Activar"}
            </button>
          </div>
          {error && (
            <p style={{ fontSize: 12, color: "#E24B4A", margin: "6px 0 0" }}>{error}</p>
          )}
          {exito && (
            <p style={{ fontSize: 12, color: "#1D9E75", margin: "6px 0 0", fontWeight: 500 }}>{exito}</p>
          )}
        </div>

        <button
          onClick={onClose}
          style={{
            width: "100%", padding: "10px 0", borderRadius: 9, marginTop: "1rem",
            border: "1px solid #E4E6F5", background: "transparent",
            fontSize: 14, color: "#666", cursor: "pointer",
          }}
        >
          Cerrar
        </button>
      </div>
    </div>
  );
}
